import { Link } from "react-router-dom";

import { AUTH_PATHS } from "../../../constants";
import type { Specialty } from "../../../types/auth";
import { PasswordInput, SelectInput, TextInput } from "../../shared/FormInputs";
import { FormErrorBanner, ProfessionalRadioGroup, SubmitButton } from "../shared";

export interface FormErrors {
  email?: string;
  password?: string;
  name?: string;
  organizationName?: string;
  specialty?: string;
  general?: string;
}

interface SignUpViewProps {
  email: string;
  password: string;
  name: string;
  organizationName: string;
  isProfessional: boolean;
  specialtyId: string;
  specialties: Specialty[];
  isSubmitting: boolean;
  errors: FormErrors;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onNameChange: (value: string) => void;
  onOrganizationNameChange: (value: string) => void;
  onIsProfessionalChange: (checked: boolean) => void;
  onSpecialtyIdChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
}

export const SignUpView = ({
  email,
  password,
  name,
  organizationName,
  isProfessional,
  specialtyId,
  specialties,
  isSubmitting,
  errors,
  onEmailChange,
  onPasswordChange,
  onNameChange,
  onOrganizationNameChange,
  onIsProfessionalChange,
  onSpecialtyIdChange,
  onSubmit,
}: SignUpViewProps) => {
  const specialtyOptions = specialties.map((s) => ({
    value: s.specialty_id,
    label: s.specialty_name,
  }));

  return (
    <div>
      <div className="mb-8">
        <h2 className="mb-2 text-3xl font-bold text-foreground">Create Account</h2>
        <p className="text-muted-foreground">Join Paribus AI and start your journey today.</p>
      </div>

      <form onSubmit={onSubmit}>
        <div>
          <TextInput
            label="Name"
            value={name}
            placeholder="Your full name"
            onChange={onNameChange}
            autoComplete="name"
            required
            error={errors.name}
          />
          <TextInput
            type="email"
            label="Email Address"
            value={email}
            placeholder="Enter your email"
            onChange={onEmailChange}
            autoComplete="email"
            required
            error={errors.email}
          />
          <PasswordInput
            label="Password"
            value={password}
            placeholder="Create a strong password"
            onChange={onPasswordChange}
            autoComplete="new-password"
            required
            error={errors.password}
          />
          <TextInput
            label="Organization Name"
            value={organizationName}
            placeholder="Your organization"
            onChange={onOrganizationNameChange}
            autoComplete="organization"
            required
            error={errors.organizationName}
          />

          <ProfessionalRadioGroup
            isProfessional={isProfessional}
            onIsProfessionalChange={onIsProfessionalChange}
          />

          {isProfessional && (
            <div className="duration-300 animate-in fade-in slide-in-from-top-2">
              <SelectInput
                label="Specialty"
                value={specialtyId}
                options={specialtyOptions}
                placeholder="Select your specialty"
                onChange={onSpecialtyIdChange}
                required={isProfessional}
                error={errors.specialty}
              />
            </div>
          )}
        </div>

        <FormErrorBanner message={errors.general} />

        <div className="mt-10 space-y-6">
          <SubmitButton isSubmitting={isSubmitting}>Sign Up</SubmitButton>

          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link
              to={AUTH_PATHS.SIGNIN}
              className="font-semibold text-primary transition-colors hover:text-primary-hover"
            >
              Sign in
            </Link>
          </p>
        </div>
      </form>
    </div>
  );
};
